/**
 * readingSync — mirrors ManualReadingStore (IndexedDB) to the Pi's
 * /api/userdata `parameter-logs` namespace so manual test readings survive
 * a browser clear and follow the operator across devices.
 *
 * Strategy: on first call, reconcile server ↔ IndexedDB by id-union
 * (local-only readings are never dropped; on an id collision the newer
 * timestamp wins), then push a debounced full snapshot to the Pi after
 * every store mutation. All Pi I/O is best-effort — offline/locked
 * failures are logged and never block the UI.
 */
import type { ManualReading } from '@reefapp/shared';
import { userDataApi } from '../lib/userdata-api';
import { list, putMany, subscribe } from './ManualReadingStore';

const NAMESPACE = 'parameter-logs';

// ─── Module-level singleton state ──────────────────────────────────────

let debounceMs = 1_000;
let unsubscribe: (() => void) | null = null;
/** Set only after a successful initial reconcile with the Pi. */
let reconciled = false;
let reconcileInFlight = false;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
/**
 * Canonical fingerprint of the last state pushed to (or confirmed on) the
 * Pi. Snapshots matching it are skipped, which suppresses the echo PUT
 * that reconcile's own IndexedDB writes would otherwise trigger.
 */
let lastSyncedCanon: string | null = null;
/** True after a warned PUT failure; reset on the next success. */
let putWarnedThisBurst = false;

// ─── Validation (unknown → ManualReading[]) ────────────────────────────

function isManualReading(v: unknown): v is ManualReading {
  if (typeof v !== 'object' || v === null) return false;
  const r = v as Record<string, unknown>;
  return (
    typeof r.id === 'string' &&
    typeof r.timestamp === 'string' &&
    typeof r.kitId === 'string' &&
    typeof r.brand === 'string' &&
    typeof r.parameter === 'string' &&
    typeof r.value === 'number' &&
    Number.isFinite(r.value) &&
    typeof r.unit === 'string' &&
    (r.notes === undefined || typeof r.notes === 'string') &&
    (r.photoBlobId === undefined || typeof r.photoBlobId === 'string') &&
    (r.reagentBatchId === undefined || typeof r.reagentBatchId === 'string')
  );
}

/** Accepts `{ readings: [...] }` (canonical) or a bare array (tolerated). */
function parseServerReadings(data: unknown): ManualReading[] {
  let candidates: unknown[] = [];
  if (Array.isArray(data)) {
    candidates = data;
  } else if (typeof data === 'object' && data !== null && 'readings' in data) {
    const readings = (data as { readings: unknown }).readings;
    if (Array.isArray(readings)) candidates = readings;
  }
  return candidates.filter(isManualReading);
}

// ─── Canonical fingerprints ────────────────────────────────────────────

function canonical(r: ManualReading): string {
  return JSON.stringify([
    r.id,
    r.timestamp,
    r.kitId,
    r.brand,
    r.parameter,
    r.value,
    r.unit,
    r.notes ?? null,
    r.photoBlobId ?? null,
    r.reagentBatchId ?? null,
  ]);
}

function canonicalSet(readings: readonly ManualReading[]): string {
  return readings.map(canonical).sort().join('\n');
}

function isNewer(candidate: ManualReading, current: ManualReading): boolean {
  const a = Date.parse(candidate.timestamp);
  const b = Date.parse(current.timestamp);
  return Number.isFinite(a) && Number.isFinite(b) && a > b;
}

// ─── Pi push (best-effort, never throws) ───────────────────────────────

async function pushToPi(readings: readonly ManualReading[]): Promise<void> {
  // Optimistically record what we're sending so the snapshot echoed by our
  // own IndexedDB writes (and identical re-notifications) is skipped.
  lastSyncedCanon = canonicalSet(readings);
  const res = await userDataApi.put(NAMESPACE, { readings: [...readings] });
  if (!res.ok) {
    lastSyncedCanon = null; // force a retry on the next store snapshot
    if (!putWarnedThisBurst) {
      putWarnedThisBurst = true;
      console.warn(
        `ReefApp: could not save parameter logs to the Pi (${res.error}); ` +
          'will retry on the next change.',
      );
    }
    return;
  }
  putWarnedThisBurst = false;
}

// ─── Store subscription → debounced push ───────────────────────────────

function onSnapshot(snapshot: readonly ManualReading[]): void {
  if (!reconciled) return;
  if (canonicalSet(snapshot) === lastSyncedCanon) return;
  if (debounceTimer !== null) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void pushToPi(snapshot);
  }, debounceMs);
}

// ─── Initial reconcile ─────────────────────────────────────────────────

async function reconcile(): Promise<void> {
  const res = await userDataApi.get(NAMESPACE);
  if (!res.ok) {
    // Offline / Pi unreachable — keep `reconciled` false so the next
    // ensureParameterLogSync() call retries.
    console.warn(
      `ReefApp: could not load parameter logs from the Pi (${res.error}); ` +
        'will retry later.',
    );
    return;
  }

  const server = parseServerReadings(res.value.data);
  const local = await list();

  if (server.length === 0) {
    if (local.length === 0) {
      lastSyncedCanon = canonicalSet([]);
      reconciled = true;
      return;
    }
    // Server empty/absent, IndexedDB has data → seed the Pi.
    reconciled = true;
    await pushToPi(local);
    return;
  }

  // Id-union merge: never drop local-only readings; on a shared id keep
  // the newer timestamp (ties/unparseable → keep local).
  const byId = new Map<string, ManualReading>(local.map((r) => [r.id, r]));
  const toWrite: ManualReading[] = [];
  for (const serverReading of server) {
    const mine = byId.get(serverReading.id);
    if (!mine) {
      byId.set(serverReading.id, serverReading);
      toWrite.push(serverReading);
    } else if (
      canonical(mine) !== canonical(serverReading) &&
      isNewer(serverReading, mine)
    ) {
      byId.set(serverReading.id, serverReading);
      toWrite.push(serverReading);
    }
  }

  if (toWrite.length > 0) {
    await putMany(toWrite);
  }

  const merged = [...byId.values()].sort((a, b) =>
    b.timestamp.localeCompare(a.timestamp),
  );
  const mergedCanon = canonicalSet(merged);
  const identicalToServer = mergedCanon === canonicalSet(server);

  if (identicalToServer) {
    lastSyncedCanon = mergedCanon; // server already has exactly this
  }
  reconciled = true;
  if (!identicalToServer) {
    await pushToPi(merged);
  }
}

// ─── Public API ────────────────────────────────────────────────────────

/**
 * Idempotent: safe to call from every page that reads or writes manual
 * readings. First successful call reconciles the Pi with IndexedDB and
 * starts mirroring; failed reconciles (offline) are retried on the next
 * call.
 */
export function ensureParameterLogSync(): void {
  if (!unsubscribe) {
    unsubscribe = subscribe(onSnapshot);
  }
  if (!reconciled && !reconcileInFlight) {
    reconcileInFlight = true;
    void reconcile()
      .catch((err: unknown) => {
        console.warn('ReefApp: parameter log sync failed', err);
      })
      .finally(() => {
        reconcileInFlight = false;
      });
  }
}

/** Test-only: tear down the singleton so each test starts clean. */
export function __resetParameterLogSyncForTests(nextDebounceMs = 1_000): void {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  reconciled = false;
  reconcileInFlight = false;
  lastSyncedCanon = null;
  putWarnedThisBurst = false;
  debounceMs = nextDebounceMs;
}
